import {
  createSignal,
  createMemo,
  createEffect,
  createResource,
  onMount,
  onCleanup,
  For,
  Index,
  Show,
  untrack,
  type JSX,
} from "solid-js";
import clsx from "~/util/html/style";
import { makeInputClass } from "~/common/input";
import { IconButton } from "~/common/button";
import { addToast } from "~/common/toast_manager";
import { Floating } from "~/common/floating";
import { RemovableChip } from "~/common/removable_chip";
import { fetchErrorMessage } from "~/request";

export type ComboboxProps<TItem, TValue> = {
  items: TItem[];
  value: TValue[];
  getValue: (item: TItem) => TValue;
  renderChipContent: (item: TItem) => JSX.Element | string;
  /** Content gets the full row width and cannot widen the list, so anything that would overflow
   *  has to clip itself (`min-w-0` + `truncate`) rather than rely on the row to stop it. */
  renderListItemContent: (item: TItem) => JSX.Element | string;
  onChange: (values: TValue[]) => void;
  /** filter and sort items based on query */
  searchFn?: (items: TItem[], query: string) => TItem[];
  /** Heading text for the item's group; a heading renders above the first item of each run, but
   *  only while the filtered list spans more than one group — a lone heading over a homogeneous
   *  list is noise, so a query that narrows the list to one group drops the headings with it. */
  groupLabel?: (item: TItem) => string;
  /** Items this rejects are never offered in the list, but still render their chip when they are already
   *  selected — how a retired option stays visible and removable without being pickable again. */
  selectable?: (item: TItem) => boolean;
  /** called when the input value changes; use for async/remote search (e.g. fetch options and set items) */
  onQueryChange?: (query: string) => void;
  /** when true, only one value can be selected; selecting replaces the current selection and closes the list */
  single?: boolean;
  /** when true, the same value can be selected more than once; removing a chip removes only that occurrence */
  allowDuplicates?: boolean;
  placeholder?: string;
  /** Read-only: the selection stays visible, nothing can be added or removed. */
  disabled?: boolean;
  /** id of the text input, so a field label can point at it */
  inputId?: string;
  class?: string;
  width?: "full" | "unset" | "fit";
  listWidth?: "max-input";
  listClass?: string;
  /** The items name patients: the input's shown value, the chips and the list rows carry
   *  data-sensitive, so Blur sensitive data (features/dev_tools) covers what the picker shows. */
  sensitive?: boolean;
};

export function Combobox<TItem, TValue>(props: ComboboxProps<TItem, TValue>) {
  const [inputValue, setInputValue] = createSignal("");
  const [isOpen, setIsOpen] = createSignal(false);
  const [highlightedIndex, setHighlightedIndex] = createSignal(0);

  let containerRef!: HTMLDivElement;
  let inputRef!: HTMLInputElement;
  let floatingRef!: HTMLDivElement;
  // The blur handler's delayed close, cleared on unmount so it never runs
  // against a page that is gone (it read `document` after a test's teardown).
  let blurTimer: ReturnType<typeof setTimeout> | undefined;
  onCleanup(() => clearTimeout(blurTimer));

  const valueToItemMap = createMemo(() => {
    const map = new Map<TValue, TItem>();
    for (const item of props.items) {
      map.set(props.getValue(item), item);
    }
    return map;
  });

  const filteredItems = createMemo(() => {
    const query = inputValue().trim();
    const selectedValues = new Set(props.value);
    const offerableItems = props.selectable ? props.items.filter(props.selectable) : props.items;

    // In single mode, keep all items (including selected) so re-selection works;
    // with duplicates allowed, selected items stay pickable again
    const baseItems =
      props.single || props.allowDuplicates
        ? offerableItems
        : offerableItems.filter((item) => !selectedValues.has(props.getValue(item)));

    if (query && props.searchFn) {
      return props.searchFn(baseItems, query);
    }

    return baseItems;
  });

  // Headings earn their place only while the list on screen spans more than one group.
  const visibleGroupLabel = createMemo(() => {
    const label = props.groupLabel;
    if (!label) return undefined;
    return new Set(filteredItems().map((item) => label(item))).size > 1 ? label : undefined;
  });

  const firstSelectedItem = () =>
    props.value.length > 0 ? valueToItemMap().get(props.value[0]!) : undefined;

  // In single mode, the input doubles as the display text for the selection.
  function restoreSingleDisplayText() {
    const item = firstSelectedItem();
    if (item) {
      const content = props.renderChipContent(item);
      setInputValue(typeof content === "string" ? content : "");
    } else {
      setInputValue("");
    }
  }

  const isListVisible = createMemo(() => isOpen() && filteredItems().length > 0);

  // In single mode, sync input display text with the selected item
  createEffect(() => {
    if (!props.single) return;
    const item = firstSelectedItem();
    if (untrack(() => isOpen())) return;
    if (item) {
      const content = props.renderChipContent(item);
      setInputValue(typeof content === "string" ? content : "");
    } else if (props.value.length === 0) {
      setInputValue("");
    }
  });

  // reset highlighted index when filtered items change
  createEffect(() => {
    void filteredItems();
    setHighlightedIndex(0);
  });

  // sync floating width with input width
  createEffect(() => {
    const open = isListVisible();

    if (open) {
      const containerWidth = containerRef.offsetWidth;
      floatingRef.style.setProperty("--reference-width", `${containerWidth}px`);
    }
  });

  onMount(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!isOpen()) {
        return;
      }

      const target = event.target as Node;

      if (containerRef.contains(target) || floatingRef.contains(target)) {
        return;
      }

      setIsOpen(false);
      // In single mode, restore display text when clicking outside
      if (props.single) {
        restoreSingleDisplayText();
      }
    }

    document.addEventListener("click", handleClickOutside, true);

    onCleanup(() => {
      document.removeEventListener("click", handleClickOutside, true);
    });
  });
  function selectItem(item: TItem) {
    const value = props.getValue(item);
    const currentValue = props.value;

    if (props.single) {
      if (!currentValue.includes(value)) {
        props.onChange([value]);
      }
      const content = props.renderChipContent(item);
      setInputValue(typeof content === "string" ? content : "");
      setIsOpen(false);
    } else {
      if (props.allowDuplicates || !currentValue.includes(value)) {
        props.onChange([...currentValue, value]);
      }
      setInputValue("");
    }
    inputRef.focus();
  }

  return (
    <div
      ref={containerRef}
      class={clsx(
        // py-1 keeps one row of chips or text inside min-h-input, the height of every other field.
        "group/combobox flex min-w-0 flex-wrap items-center gap-1 py-1",
        makeInputClass({ compact: false, py: "unset" }),
        props.width === "full" && "w-full",
        props.width === "fit" && "w-fit",
        props.class,
      )}
      onClick={() => inputRef.focus()}
    >
      <Show when={!props.single}>
        {/* Chips are keyed by position, so a repeated value (allowDuplicates) removes
            only its own occurrence and item references stay stable across renders. */}
        <Index each={props.value}>
          {(value, valueIndex) => (
            <Show when={valueToItemMap().get(value())} keyed>
              {(item) => {
                const content = props.renderChipContent(item);
                return (
                  <RemovableChip
                    title={typeof content === "string" ? content : undefined}
                    disabled={props.disabled}
                    sensitive={props.sensitive}
                    onRemove={() =>
                      props.onChange(props.value.filter((_, index) => index !== valueIndex))
                    }
                  >
                    {content}
                  </RemovableChip>
                );
              }}
            </Show>
          )}
        </Index>
      </Show>

      <input
        ref={inputRef}
        id={props.inputId}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isListVisible()}
        autocomplete="off"
        value={inputValue()}
        disabled={props.disabled}
        data-sensitive={props.sensitive ? "redact" : undefined}
        placeholder={props.value.length === 0 ? props.placeholder : undefined}
        class="min-w-[120px] flex-1 border-none bg-transparent p-0 focus:ring-0 focus:outline-none"
        onFocus={() => {
          setIsOpen(true);
          if (props.single && props.value.length > 0) {
            setTimeout(() => inputRef.select(), 0);
          }
        }}
        onBlur={() => {
          // delay to allow click events on items to fire first
          clearTimeout(blurTimer);
          blurTimer = setTimeout(() => {
            if (!floatingRef.contains(document.activeElement)) {
              setIsOpen(false);
              // In single mode, restore display text on blur
              if (props.single) {
                restoreSingleDisplayText();
              }
            }
          }, 100);
        }}
        onInput={(event) => {
          const target = event.target as HTMLInputElement;
          const value = target.value;
          setInputValue(value);
          setIsOpen(true);
          props.onQueryChange?.(value);
        }}
        onKeyDown={(event: KeyboardEvent) => {
          const filtered = filteredItems();
          const filteredLength = filtered.length;

          if (event.key === "ArrowDown") {
            event.preventDefault();
            if (filteredLength > 0) {
              setIsOpen(true);
              setHighlightedIndex((prev) => (prev + 1) % filteredLength);
            }
            return;
          }

          if (event.key === "ArrowUp") {
            event.preventDefault();
            if (filteredLength > 0) {
              setIsOpen(true);
              setHighlightedIndex((prev) => (prev + filteredLength - 1) % filteredLength);
            }
            return;
          }

          if (event.key === "Enter") {
            event.preventDefault();
            if (filteredLength > 0) {
              const highlighted = filtered[highlightedIndex()];
              if (highlighted) {
                selectItem(highlighted);
              }
            }
            return;
          }

          if (event.key === "Escape") {
            event.preventDefault();
            setIsOpen(false);
            inputRef.blur();
            return;
          }

          if (event.key === "Backspace" && inputValue() === "" && !props.single) {
            event.preventDefault();
            const currentValue = props.value;
            if (currentValue.length > 0) {
              props.onChange(currentValue.slice(0, -1));
            }
            return;
          }
        }}
      />

      <Show when={props.single && props.value.length > 0 && !props.disabled}>
        <IconButton
          variant="plain"
          size="xs"
          iconName="x"
          aria-label="Clear selection"
          onClick={(e) => {
            e.stopPropagation();
            props.onChange([]);
            setInputValue("");
            props.onQueryChange?.("");
            inputRef.focus();
          }}
        />
      </Show>

      <Floating
        reference={() => containerRef}
        open={isListVisible}
        placement="bottom-start"
        floatingRef={(element) => {
          floatingRef = element;
        }}
      >
        <div
          class={clsx(
            "max-h-64 overflow-auto overscroll-none rounded-xl border border-brand-divider bg-white text-sm shadow-lg",
            props.listClass,
          )}
          style={{
            "min-width": "var(--reference-width)",
            "max-width": props.listWidth === "max-input" ? "var(--reference-width)" : undefined,
          }}
        >
          {/* The heading is a sibling inside this callback rather than its own list, so index()
              still maps 1:1 onto filteredItems() and highlightedIndex. */}
          <For each={filteredItems()}>
            {(item, index) => {
              const heading = () => {
                const label = visibleGroupLabel();
                if (!label) return undefined;
                const text = label(item);
                const previous = filteredItems()[index() - 1];
                return previous && text === label(previous) ? undefined : text;
              };

              return (
                <>
                  {/* Sticky so the run a long list is scrolled into stays named. */}
                  <Show when={heading()}>
                    {(text) => (
                      <div class="sticky top-0 bg-white px-3 py-2 text-sm text-typography-secondary">
                        {text()}
                      </div>
                    )}
                  </Show>
                  <button
                    type="button"
                    class={clsx(
                      "flex w-full cursor-pointer items-center px-3 py-2 text-left transition-colors",
                      index() === highlightedIndex() ? "bg-brand-light" : "hover:bg-brand-hover",
                    )}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectItem(item);
                    }}
                    onMouseEnter={() => setHighlightedIndex(index())}
                  >
                    {/* min-w-0 because a flex item's automatic minimum size is its content. */}
                    <span
                      class="w-full min-w-0 text-typography-primary"
                      data-sensitive={props.sensitive ? "true" : undefined}
                    >
                      {props.renderListItemContent(item)}
                    </span>
                  </button>
                </>
              );
            }}
          </For>
        </div>
      </Floating>
    </div>
  );
}

type AsyncComboboxConfig<TItem, TValue> = {
  /** The currently-selected items (reactive). */
  selected: () => TItem[];
  getValue: (item: TItem) => TValue;
  loadOptions: (query: string) => Promise<TItem[]>;
  onChange: (selected: TItem[]) => void;
  /** 0 loads (and shows) loadOptions("") up front, so the list is ready before the user types. */
  minQueryLength?: number;
  /** A reactive scope (e.g. a parent id) that loadOptions reads but isn't part of the query text —
   *  changing it re-runs the current query so a stale, differently-scoped list isn't shown. */
  reloadKey?: () => string;
  errorMessage?: string;
};

// Async data plumbing for a Combobox: spread the returned props onto one rather
// than reaching for a separate async component. createResource handles the fetch and discards
// out-of-order responses; selected items stay in the list so their chip/label keeps rendering
// after results change.
export function createAsyncCombobox<TItem, TValue>(config: AsyncComboboxConfig<TItem, TValue>) {
  const [query, setQuery] = createSignal("");
  const minLength = () => config.minQueryLength ?? 2;

  const [results] = createResource(
    () => {
      const trimmed = query().trim();
      if (trimmed.length < minLength()) return null;
      // scope isn't part of loadOptions query text, but must still bust the cache when it changes.
      return { query: trimmed, scope: config.reloadKey?.() ?? "" };
    },
    (source) => config.loadOptions(source.query),
  );

  createEffect(() => {
    if (results.error) {
      addToast({
        type: "error",
        text: fetchErrorMessage(results.error, config.errorMessage ?? "Search failed"),
      });
    }
  });

  const items = createMemo(() => {
    const selectedValues = new Set(config.selected().map(config.getValue));
    return [
      ...config.selected(),
      // Reading an errored resource throws, which would take the whole page down to its error
      // boundary; the effect above has already told the user the search failed.
      ...(results.error ? [] : (results() ?? [])).filter(
        (item) => !selectedValues.has(config.getValue(item)),
      ),
    ];
  });
  const value = createMemo(() => config.selected().map(config.getValue));

  return {
    getValue: config.getValue,
    get items() {
      return items();
    },
    get value() {
      return value();
    },
    onQueryChange: setQuery,
    onChange: (values: TValue[]) => {
      const byValue = new Map(items().map((item) => [config.getValue(item), item]));
      config.onChange(
        values.map((v) => byValue.get(v)).filter((item): item is TItem => item !== undefined),
      );
    },
  };
}
